import { create } from 'zustand';
import { storage } from '@/lib/storage';

export type Theme = 'light' | 'dark';

export interface RecentEntry {
  id: string;
  toolId: string;
  toolName: string;
  category: string;
  ts: number;
}

interface AppState {
  theme: Theme;
  paletteOpen: boolean;
  agentOpen: boolean;
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  favorites: string[];
  recents: RecentEntry[];
  pendingFile: File | null;

  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  setPaletteOpen: (v: boolean) => void;
  togglePalette: () => void;
  setAgentOpen: (v: boolean) => void;
  toggleAgent: () => void;
  setSidebarCollapsed: (v: boolean) => void;
  toggleSidebar: () => void;
  setMobileSidebarOpen: (v: boolean) => void;
  toggleMobileSidebar: () => void;
  toggleFavorite: (toolId: string) => void;
  pushRecent: (entry: Omit<RecentEntry, 'id' | 'ts'>) => void;
  clearRecents: () => void;
  setPendingFile: (f: File | null) => void;
}

export const useApp = create<AppState>((set, get) => ({
  theme: storage.get<Theme>('theme', 'light'),
  paletteOpen: false,
  agentOpen: false,
  sidebarCollapsed: storage.get<boolean>('sidebarCollapsed', false),
  mobileSidebarOpen: false,
  favorites: storage.get<string[]>('favorites', []),
  recents: storage.get<RecentEntry[]>('recents', []),
  pendingFile: null,

  setTheme: (t) => {
    set({ theme: t });
    storage.set('theme', t);
    if (typeof document !== 'undefined') {
      document.documentElement.classList.toggle('dark', t === 'dark');
    }
  },
  toggleTheme: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    get().setTheme(next);
  },

  setPaletteOpen: (v) => set({ paletteOpen: v }),
  togglePalette: () => set((s) => ({ paletteOpen: !s.paletteOpen })),

  setAgentOpen: (v) => set({ agentOpen: v }),
  toggleAgent: () => set((s) => ({ agentOpen: !s.agentOpen })),

  setSidebarCollapsed: (v) => {
    set({ sidebarCollapsed: v });
    storage.set('sidebarCollapsed', v);
  },
  toggleSidebar: () => {
    const next = !get().sidebarCollapsed;
    get().setSidebarCollapsed(next);
  },

  setMobileSidebarOpen: (v) => set({ mobileSidebarOpen: v }),
  toggleMobileSidebar: () => set((s) => ({ mobileSidebarOpen: !s.mobileSidebarOpen })),

  toggleFavorite: (toolId) => {
    const list = get().favorites;
    const next = list.includes(toolId) ? list.filter((id) => id !== toolId) : [...list, toolId];
    set({ favorites: next });
    storage.set('favorites', next);
  },

  pushRecent: (entry) => {
    const list = get().recents;
    const newEntry: RecentEntry = {
      ...entry,
      id: `${entry.toolId}-${Date.now()}`,
      ts: Date.now(),
    };
    const filtered = list.filter((r) => r.toolId !== entry.toolId);
    const next = [newEntry, ...filtered].slice(0, 24);
    set({ recents: next });
    storage.set('recents', next);
  },

  clearRecents: () => {
    set({ recents: [] });
    storage.set('recents', []);
  },

  setPendingFile: (f) => set({ pendingFile: f }),
}));
